import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from 'react-bootstrap';
import { API_URL } from '../config';
import { formatDateOnly } from '../utils/dateDisplay';
import './StudentAttendancePanel.css';

const EMPTY_SUMMARY = { total: 0, present: 0, absent: 0, excused: 0, late: 0, presentRate: 0 };

export default function StudentAttendancePanel({ studentId }) {
  const [attendanceData, setAttendanceData] = useState({ summary: EMPTY_SUMMARY, records: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  const loadAttendance = async () => {
    if (!studentId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API_URL}/attendance/student/${studentId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setAttendanceData({
        summary: { ...EMPTY_SUMMARY, ...(data?.summary || {}) },
        records: Array.isArray(data?.records) ? data.records : [],
      });
    } catch (requestError) {
      console.error('Load student attendance failed', requestError);
      setError(requestError?.response?.data?.error || 'Could not load attendance records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setFilter('all');
    setAttendanceData({ summary: EMPTY_SUMMARY, records: [] });
    loadAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const records = useMemo(() => {
    if (filter === 'all') return attendanceData.records;
    if (filter === 'late') return attendanceData.records.filter((record) => Boolean(record.is_late));
    return attendanceData.records.filter((record) => record.status === filter);
  }, [attendanceData.records, filter]);

  const summaryItems = [
    { key: 'presentRate', label: 'Attendance rate', value: `${attendanceData.summary.presentRate}%`, icon: 'graph-up-arrow' },
    { key: 'present', label: 'Present', value: attendanceData.summary.present, icon: 'check-circle' },
    { key: 'absent', label: 'Absent', value: attendanceData.summary.absent, icon: 'x-circle' },
    { key: 'late', label: 'Late', value: attendanceData.summary.late, icon: 'clock' },
  ];

  if (loading) {
    return <div className="student-attendance-state" role="status"><span className="spinner-border spinner-border-sm" /> Loading attendance…</div>;
  }

  if (error) {
    return (
      <div className="student-attendance-state student-attendance-state--error" role="alert">
        <i className="bi bi-exclamation-circle" aria-hidden="true" />
        <strong>{error}</strong>
        <Button size="sm" variant="outline-danger" onClick={loadAttendance}>Try again</Button>
      </div>
    );
  }

  return (
    <section className="student-attendance" aria-labelledby={`student-attendance-title-${studentId}`}>
      <div className="student-attendance__heading">
        <div>
          <span>Attendance</span>
          <h3 id={`student-attendance-title-${studentId}`}>Attendance records</h3>
          <p>The latest {attendanceData.summary.total} attendance {attendanceData.summary.total === 1 ? 'record' : 'records'}.</p>
        </div>
      </div>

      <div className="student-attendance__summary">
        {summaryItems.map((item) => (
          <div className={`student-attendance-stat student-attendance-stat--${item.key}`} key={item.key}>
            <i className={`bi bi-${item.icon}`} aria-hidden="true" />
            <div><strong>{item.value}</strong><span>{item.label}</span></div>
          </div>
        ))}
      </div>

      {attendanceData.records.length === 0 ? (
        <div className="student-attendance-state">
          <i className="bi bi-calendar2-check" aria-hidden="true" />
          <strong>No attendance recorded yet</strong>
          <span>Attendance will appear here after it is submitted.</span>
        </div>
      ) : (
        <>
          <div className="student-attendance__filters" aria-label="Filter attendance records">
            {[
              ['all', 'All'],
              ['present', 'Present'],
              ['absent', 'Absent'],
              ['excused', 'Excused'],
              ['late', 'Late'],
            ].map(([value, label]) => (
              <button type="button" key={value} className={filter === value ? 'is-active' : ''} aria-pressed={filter === value} onClick={() => setFilter(value)}>{label}</button>
            ))}
          </div>

          {records.length === 0 ? (
            <div className="student-attendance-state">No records match this filter.</div>
          ) : (
            <div className="student-attendance__records">
              {records.map((record) => (
                <article className="student-attendance-record" key={record.id}>
                  <div className={`student-attendance-record__status student-attendance-record__status--${record.status}`}>
                    <i className={`bi bi-${record.status === 'present' ? 'check' : record.status === 'absent' ? 'x' : 'shield-check'}`} aria-hidden="true" />
                  </div>
                  <div className="student-attendance-record__main">
                    <div><strong>{formatDateOnly(record.date)}</strong><span>{record.class_name || 'Class'}</span></div>
                    {record.comment && <p>{record.comment}</p>}
                  </div>
                  <div className="student-attendance-record__badges">
                    <span className={`student-attendance-badge student-attendance-badge--${record.status}`}>{record.status}</span>
                    {Boolean(record.is_late) && <span className="student-attendance-badge student-attendance-badge--late">Late</span>}
                  </div>
                </article>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
